import {
  CcfBox,
  CcfChatIcon,
  CcfEmailIcon,
  CcfPhoneInboundIcon,
  CcfServiceBotIcon,
  CcfSmsIcon,
  CcfTypography,
  useTranslator,
} from '@nice-ccf/ui-controls';
import styles from './ccf-consumer-card-interactions-table-view.module.scss';
import { DataGrid } from '@mui/x-data-grid';
import { makeStyles } from '@mui/styles';
import { Avatar, Icon } from '@mui/material';
import { useCallback, useMemo } from 'react';
import React from 'react';
import { useSelector } from 'react-redux';
import { activeContactSelector } from '../../../../ccf-inbox/ccf-inbox.slice';

/* eslint-disable-next-line */
export interface CcfConsumerCardInteractionsTableViewProps {
  interactionsHistoryData: any;
  interactionViewSelectHandler: (view: string, interaction: any) => void;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    '& .MuiDataGrid-root': {
      border: '1px solid rgba(251, 252, 252, 1)',
      // Height: '330px',
      flexGrow: 1,
    },
    '& .MuiDataGrid-cell': {
      fontFamily: 'Open Sans',
      fontSize: '12px',
      lineHeight: '24px',
    },
    '& .MuiDataGrid-row': {
      backgroundColor: '#FFFFFF',
      borderBottom: '1px solid #cbd3d4',
    },

    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: '0px',
      fontFamily: 'Open Sans',
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: '12px',
      lineHeight: '16px',
      textTransform: 'uppercase',
      color: '#526B79',
    },
    '& .MuiDataGrid-columnsContainer': {
      background: '#FFFFF',
      border: '1px solid #D2D8DB',
      boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.08)',
    },
    '& .styledrows': {
      height: '40px',
      background: '#FFFFFF',
      // border: '1px solid #D2D8DB',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      fontFamily: 'Open Sans',
      fontStyle: 'normal',
      fontWeight: '400',
      fontSize: '13px',
      lineHeight: '18px',
      color: '#333333',
    },
    '& .MuiDataGrid-columnHeaders': {
      border: '1px solid #cbd3d4',
    },
    '& .MuiDataGrid-footerContainer': {
      backgroundColor: '#FFFFFF',
      border: '2px solid #d1d9da',
    },
    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
      {
        outline: 'none',
      },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .custom-bold': {
      fontWeight: 'bold',
    },
    '& .custom-link': {
      color: '#005EB8',
      textDecoration: 'underline',
      cursor: 'pointer',
      pointerEvents: 'auto',
    },
  },
});

/**
 * CcfConsumerCardInteractionsTableView - returns Interactions Tab Table View
 * @param props - CcfConsumerCardInteractionsTableViewProps
 * @returns - appspace
 * @example - `<CcfConsumerCardInteractionsTableView />`
 */
export function CcfConsumerCardInteractionsTableView(
  props: CcfConsumerCardInteractionsTableViewProps
) {
  const classes = useStyles();
  const { interactionsHistoryData, interactionViewSelectHandler } = props;
  const rowsPerPageOptions = [5, 10, 20];
  const [pageSize, setPageSize] = React.useState(rowsPerPageOptions[1]);
  const [translate] = useTranslator();
  const activeContact = useSelector(activeContactSelector);

  // Add a new field mediaType to each object in interactionsHistoryData
  const modifiedInteractionsHistoryData = interactionsHistoryData.map(
    (interaction: any) => ({
      ...interaction,
      mediaType: interaction.media, // Copy the value of media to mediaType
    })
  );

  const stringAvatar = useMemo(
    () => (name: string) => {
      const [firstName, lastName] = name.split(' ');
      return {
        children: `${firstName[0]}${lastName ? lastName[0] : ''}`,
      };
    },
    []
  );

  /**
   * Used to format contact history date and time
   * @param time -string
   * @example - formatDateTimeHandler()
   */
  const formatDateTimeHandler = (time: string) => {
    const date = new Date(time);

    return date.toLocaleDateString('en-US', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns = [
    {
      field: 'interactionId',
      headerName: '',
      width: 0,
      hide: true,
    },
    {
      field: 'media',
      headerName: '',
      width: 50,
      flex: 0.1,
      renderCell: (params: any) => {
        switch (params.value) {
          case 'Email':
            return <CcfEmailIcon className={styles.channelIcon} />;
          case 'Voice':
            return <CcfPhoneInboundIcon className={styles.channelIcon} />;
          case 'Chat':
            return <CcfChatIcon className={styles.channelIcon} />;
          case 'SMS':
            return <CcfSmsIcon className={styles.channelIcon} />;
          default:
            return null;
        }
      },
    },
    {
      field: 'agentName',
      headerName: translate('Agent'),
      width: 150,
      flex: 1,
      cellClassName: 'custom-bold',
      renderCell: (params: any) => {
        return params.value !== 'Service Bot' ? (
          <CcfBox className={styles.agentNameBlock}>
            <CcfBox className={styles.avatarBlock}>
              <Avatar
                alt={params.value}
                {...stringAvatar(params.value)}
                className={styles.avatar}
              />
              <span className={styles.badge} />
            </CcfBox>
            <span className={styles.ellipsis}>
              {translate('Agent')}
              {'  '}
              {params.value.split(' ')?.[1]}
            </span>
          </CcfBox>
        ) : (
          <CcfBox className={styles.agentNameBlock}>
            <Avatar className={styles.avatar}>
              <Icon>
                <CcfServiceBotIcon className={styles.botIcon} />
              </Icon>{' '}
            </Avatar>
            <CcfTypography
              className={`${styles.serviceBotTxt} ${styles.ellipsis}`}
              translationKey={'serviceBot'}
            ></CcfTypography>
          </CcfBox>
        );
      },
    },
    {
      field: 'status',
      headerName: translate('status'),
      width: 100,
      flex: 0.7,
      cellClassName: 'custom-bold',
      renderCell: (params: any) => {
        return (
          <span
            style={{
              color:
                params.value.toLowerCase() === 'open' ? '#0B82DD' : '#5f6367',
            }}
            className="pointerEvent"
          >
            {translate(params.value.toLowerCase())}
          </span>
        );
      },
    },
    {
      field: 'mediaType',
      headerName: translate('channel'),
      width: 250,
      flex: 1.5,
      cellClassName: 'custom-link',
      renderCell: (params: any) => {
        return (
          <span className="pointerEvent">
            {params.value === 'Email'
              ? activeContact?.customer.emailId
              : activeContact?.customer.phoneNo}
          </span>
        );
      },
    },
    {
      field: 'date',
      headerName: translate('Date'),
      width: 190,
      flex: 1.2,
      renderCell: (params: any) => {
        return (
          <span className="pointerEvent">
            {formatDateTimeHandler(params.value)}
          </span>
        );
      },
    },
  ];

  /**
   * Function to select interaction view
   * @example handleInteractionView()
   */
  const handleInteractionView = useCallback(
    (selectedView: string, interaction: any) => {
      interactionViewSelectHandler(selectedView, interaction);
    },
    [interactionViewSelectHandler]
  );

  /**
   * function to handle each row click on Datagrid
   * @example - handleRowClick()
   */
  const handleRowClick = (params: any) => {
    handleInteractionView('Details', params.row);
  };

  return (
    <CcfBox className={styles.tableContainer}>
      <DataGrid
        classes={{ root: classes.root }}
        rows={modifiedInteractionsHistoryData}
        columns={columns}
        rowHeight={40}
        headerHeight={40}
        pageSize={pageSize}
        pagination
        rowsPerPageOptions={rowsPerPageOptions}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        getRowId={(row) => row.interactionId}
        getRowClassName={(params: any) => `styledrows`}
        onRowClick={handleRowClick}
        sx={{
          '& .MuiDataGrid-columnHeaderTitle': {
            color: '#3F5C69',
            fontWeight: '600',
            fontSize: '12px',
            fontFamily: 'Open Sans',
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: '1px solid #D2D8DB',
          },
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: 'none',
          },
          '& .MuiDataGrid-columnHeader:last-child': {
            borderRight: 'none',
          },
        }}
      />
    </CcfBox>
  );
}

export default CcfConsumerCardInteractionsTableView;
